'use client';

import { useEffect, useRef } from 'react';

import {
  DwellTracker,
  SCROLL_MILESTONES,
  ScrollDepthTracker,
} from '@onark/shared/engagement';

// Scroll/dwell effects shared by the marketing EngagementTracker and the v2
// signup shell's SignupEngagementTracker. Only the emit differs. `key` re-arms
// the effect (pathname / landing); `emit` is read through a ref so a changing
// closure never tears the listeners down.

/** Scroll-depth milestones (also checked once on mount for short pages). */
export function useScrollDepth(
  emit: (milestone: number) => void,
  key: unknown
) {
  const emitRef = useRef(emit);

  emitRef.current = emit;

  useEffect(() => {
    const tracker = new ScrollDepthTracker(SCROLL_MILESTONES);

    const onScroll = () => {
      const reached = tracker.advance({
        innerHeight: window.innerHeight,
        scrollHeight: document.documentElement.scrollHeight,
        scrollY: window.scrollY,
      });

      for (const milestone of reached) emitRef.current(milestone);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [key]);
}

/** Active foreground dwell, emitted in seconds. A background tab is not a read. */
export function useDwell(emit: (seconds: number) => void, key: unknown) {
  const emitRef = useRef(emit);

  emitRef.current = emit;

  useEffect(() => {
    const tracker = new DwellTracker(Date.now(), !document.hidden);

    const interval = window.setInterval(() => {
      for (const ms of tracker.tick(Date.now())) emitRef.current(ms / 1000);
    }, 5_000);
    const onVisibility = () => tracker.setActive(Date.now(), !document.hidden);

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [key]);
}
